import { ExtensionAvatar } from "@dashboard/extensions/components/ExtensionAvatar";
import { PluginIcon } from "@dashboard/icons/PluginIcon";
import { Box, Button, GenericAppIcon, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { extensionConfigMessages } from "./messages";

export interface ExtensionItem {
  id: string;
  name: string;
  logoUrl: string | null;
  isActive: boolean;
  type: "app" | "plugin";
}

interface ExtensionListProps {
  extensions: ExtensionItem[];
  onConfigure: (item: ExtensionItem) => void;
  emptyMessage?: string;
}

export const ExtensionList: React.FC<ExtensionListProps> = ({
  extensions,
  onConfigure,
  emptyMessage,
}) => {
  const intl = useIntl();

  if (extensions.length === 0) {
    return (
      <Box
        padding={6}
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={4}
      >
        <Text color="default2" size={3}>
          {emptyMessage ?? intl.formatMessage(extensionConfigMessages.noExtensions)}
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={5}>
      {extensions.map(ext => (
        <Box
          key={ext.id}
          display="flex"
          alignItems="center"
          gap={3}
        >
          <ExtensionAvatar>
            {ext.logoUrl ? (
              <Box as="img" src={ext.logoUrl} alt={ext.name} display="block" maxWidth="100%" />
            ) : ext.type === "plugin" ? (
              <PluginIcon />
            ) : (
              <GenericAppIcon size="medium" color="default2" />
            )}
          </ExtensionAvatar>

          <Box display="flex" flexDirection="column" gap={0.5}>
            <Text fontWeight="bold">{ext.name}</Text>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                __width="6px"
                __height="6px"
                borderRadius={4}
                __backgroundColor={ext.isActive ? "#27ae60" : "#94a3b8"}
              />
              <Text
                size={2}
                color={ext.isActive ? undefined : "default2"}
                __color={ext.isActive ? "#27ae60" : undefined}
              >
                {ext.isActive
                  ? intl.formatMessage(extensionConfigMessages.active)
                  : intl.formatMessage(extensionConfigMessages.inactive)}
              </Text>
            </Box>
          </Box>

          <Box marginLeft="auto">
            <Button variant="secondary" type="button" onClick={() => onConfigure(ext)}>
              {intl.formatMessage(extensionConfigMessages.configure)}
            </Button>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
